import { CohortType, MyCohortProgressEnum } from '@platform/client/schemas';
import type { TimelinePanelContent } from './CohortTimeline';

/** Display name for each step, used for both the timeline node title and its panel header. */
export const STEP_LABELS: Record<MyCohortProgressEnum, string> = {
  kickoff: 'Kickoff',
  matching: 'Matching',
  planning: 'Planning',
  action: 'Action',
  reflect: 'Reflect',
  wrap_up: 'Wrap Up',
};

/**
 * Cohort types grouped by the copy they share. Kickoff is the only step that distinguishes
 * assigned individuals (they already know their project); every other step splits the three
 * individual cohort types from teams.
 */
type NormalizedCohortFormat = 'individual' | 'individual-assigned' | 'teams';

const NORMALIZED_FORMAT_MAPPING: Record<CohortType, NormalizedCohortFormat> = {
  Individual: 'individual',
  'Self-Paced - Individual': 'individual',
  'Individual - Assigned': 'individual-assigned',
  Teams: 'teams',
};

type PanelBody = Omit<TimelinePanelContent, 'header' | 'cta'>;
type PanelByFormat = Record<NormalizedCohortFormat, PanelBody>;

/** For the steps whose copy is the same for every individual cohort type. */
const individualOrTeams = (individual: PanelBody, teams: PanelBody): PanelByFormat => ({
  individual,
  'individual-assigned': individual,
  teams,
});

const sameForAll = (body: PanelBody): PanelByFormat => individualOrTeams(body, body);

const PANEL_CONTENT: Record<MyCohortProgressEnum, PanelByFormat> = {
  kickoff: {
    individual: {
      subHeader:
        'Join a live call to meet your cohort, understand how the program works, and how to get started.',
      items: [
        'Get practical tips for delivering real value to an organization, alongside your day-to-day work.',
        "Get guidance on how to match with a project that's a good fit for your skills and experience.",
        'You will be introduced to the cohort platform and resources.',
      ],
    },
    'individual-assigned': {
      subHeader:
        'Join a live call to meet your cohort, understand how the program works, and how to get started.',
      items: [
        "Learn about the project you'll be working on and the organization you'll be supporting.",
        'Get practical tips for delivering real value to an organization, alongside your day-to-day work.',
        'Get all your questions answered so you feel prepared to make an impact.',
      ],
    },
    teams: {
      subHeader:
        'Join a live call to meet your cohort, understand how the program works, and how to get started.',
      items: [
        "Meet your team and learn about the project you'll be working on together.",
        'Get practical tips for delivering real value to an organization, alongside your day-to-day work.',
        'Get all your questions answered so your team feels prepared to make an impact.',
      ],
    },
  },
  // Only shown to individuals who pick their own project, but every audience needs an entry.
  // This will be filtered out for relevant audiences in `TimelineSection` so that the timeline doesn't show a step that doesn't apply.
  matching: sameForAll({
    subHeader: "Explore available projects and confirm the one you'll work on.",
    items: [
      'Respond to introductions and search for other projects that interest you.',
      "Accept an introduction and have a short Discovery call to see if you'd like to work together.",
      "Keep the platform updated throughout and confirm your match once you've found the right fit.",
    ],
  }),
  planning: individualOrTeams(
    {
      subHeader: "Work with the organization to agree on a project plan you'll both follow.",
      items: [
        "Come prepared with your own thoughts on what's realistic to take on.",
        "Agree on what you'll deliver, focusing on what will help the organization most.",
        "Decide together how you'll communicate and stay on track along the way.",
      ],
    },
    {
      subHeader:
        'Work with the organization to agree on a project plan your team and the organization will follow.',
      items: [
        "Come prepared with your team's thoughts on what's realistic to take on.",
        'Agree on what your team will deliver, focusing on what will help the organization most.',
        'Decide together how your team and the organization will communicate and stay on track along the way.',
      ],
    },
  ),
  action: individualOrTeams(
    {
      subHeader: 'Apply your skills and experience to help the organization reach its goals.',
      items: [
        'Work with the organization to execute your project plan.',
        'Adjust the plan when needed, and focus on what will make the biggest difference to the organization.',
        'Mark the project complete on the platform and share what you accomplished.',
      ],
    },
    {
      subHeader:
        "Apply your team's skills and experience to help the organization reach its goals.",
      items: [
        "Work with the organization to execute your team's project plan.",
        'Adjust the plan when needed, and focus on what will make the biggest difference to the organization.',
        'Mark the project complete on the platform and share what your team accomplished.',
      ],
    },
  ),
  reflect: individualOrTeams(
    {
      subHeader: 'Reflect on what you accomplished, and the impact it made.',
      items: [
        'Consider what you learned, whether the experience went smoothly or not.',
        'Share feedback on the organization and the program to help improve it for others.',
        'Earn a certificate you can proudly share.',
      ],
    },
    {
      subHeader: 'Reflect on what you accomplished together, and the impact it made.',
      items: [
        'Consider what you learned, whether the experience went smoothly or not.',
        'Share feedback on the organization and the program to help improve it for others.',
        'Earn a certificate you can proudly share.',
      ],
    },
  ),
  wrap_up: individualOrTeams(
    {
      subHeader: 'Join the closing event to showcase your project and connect with your cohort.',
      items: [
        'Celebrate the impact you created.',
        'See what your cohort accomplished together.',
        "Explore what's next and how to stay involved.",
      ],
    },
    {
      subHeader:
        "Join the closing event to showcase your team's project and connect with your cohort.",
      items: [
        'Celebrate the impact your team created.',
        'See what your cohort accomplished together.',
        "Explore what's next and how to stay involved.",
      ],
    },
  ),
};

/** Copy shown in the expandable panel for a given step, varied by cohort type. */
export const getPanelContent = (
  cohortType: CohortType,
  step: MyCohortProgressEnum,
): TimelinePanelContent => ({
  header: STEP_LABELS[step],
  ...PANEL_CONTENT[step][NORMALIZED_FORMAT_MAPPING[cohortType]],
});
